/**
 * URLs and names of events — one rule for the build, the edge, the jobs and the site. These
 * were copied into eight places that had to "stay in sync"; a copy that drifts sends Google
 * to a 301, a link to the wrong page, or a title with the act's name twice.
 */
import { postseasonLabel } from './postseason.mjs';
import { tidyName } from './event-title.mjs';

/** "Mötley Crüe & Friends!" → "motley-crue-friends" */
export const slugify = (/** @type {string} */ s) =>
  String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * The keyword-rich slug of an event's URL — act (or both teams, or the name), city, venue,
 * "tickets" — capped at 90 characters. The id is the real key: a page whose slug changed
 * answers with a 301 to the new one.
 * @param {{ artist?: string | null, teams?: string[] | null, name: string, city: string, venue: string }} e
 */
export const eventSlug = (e) => {
  const primary = e.artist ?? (e.teams?.length ? e.teams.join(' ') : e.name);
  return slugify(`${primary} ${e.city} ${e.venue} tickets`).slice(0, 90).replace(/-+$/, '');
};

/** /<slug>/event/<id>
 * @param {{ id: string, artist?: string | null, teams?: string[] | null, name: string, city: string, venue: string }} e */
export const eventPath = (e) => `/${eventSlug(e)}/event/${e.id}`;

/**
 * An event's name in titles, headings and schema. Most catalog names already carry the act
 * ("Rod Wave with FRIDAYY"): the act is put in front only when the name does not start with
 * it — never "Rod Wave — Rod Wave with FRIDAYY", never "Wicked — Wicked".
 * An MLB postseason game reads as people say it ("World Series Game 1: Brewers home game", not
 * SeatGeek's "TBD at Brewers - World Series (Gm 1, HG 1)"; shared/postseason.mjs) — the name
 * only: the slug, which comes from the teams, never changes with it.
 * @param {string | null | undefined} artist
 * @param {string} name
 */
export const displayName = (artist, name) => {
  const n = tidyName(String(name ?? '').trim());
  const a = tidyName(String(artist ?? '').trim());
  if (!a) return postseasonLabel(n) ?? n;
  if (n.toLowerCase().startsWith(a.toLowerCase())) return n;
  return `${a} — ${n}`;
};
